// feature-module/inventory/BarcodeGenerator.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Select from 'react-select';
import Barcode from 'react-barcode';
import { QRCodeCanvas as QRCode } from 'qrcode.react';
import jsPDF from 'jspdf';
//import html2canvas from 'html2canvas';
import {
    Printer, Download, RefreshCw, Search,
    ChevronUp, Grid, List, FileText, Eye,
    X, Camera, Code, Hash
} from 'feather-icons-react/build/IconComponents';
import { barcodeActions } from "../../core/action/barcodeActions";

const MySwal = withReactContent(Swal);

// Helper function to safely format price
const formatPrice = (price) => {
    if (price === null || price === undefined) return '0.00';
    const num = typeof price === 'string' ? parseFloat(price) : price;
    return isNaN(num) ? '0.00' : num.toFixed(2);
};

const BarcodeGenerator = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [viewMode, setViewMode] = useState('grid');
    const [copies, setCopies] = useState(1);
    const [paperSize, setPaperSize] = useState('A4');
    const [labelsPerPage, setLabelsPerPage] = useState(24);
    const [activeTab, setActiveTab] = useState('generate');
    const [printJobs, setPrintJobs] = useState([]);
    const [previewProduct, setPreviewProduct] = useState(null);
    const [showQRModal, setShowQRModal] = useState(false);
    const [qrData, setQrData] = useState(null);
    const [barcodeFormat, setBarcodeFormat] = useState('CODE128');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 12,
        total: 0,
        totalPages: 1
    });

    // Barcode format options
    const formatOptions = [
        { value: 'CODE128', label: 'CODE128 (Standard)' },
        { value: 'CODE39', label: 'CODE39' },
        { value: 'EAN13', label: 'EAN-13' },
        { value: 'EAN8', label: 'EAN-8' },
        { value: 'UPC', label: 'UPC' },
        { value: 'ISBN', label: 'ISBN' },
        { value: 'PHARMACODE', label: 'Pharmacode' },
        { value: 'CODABAR', label: 'Codabar' }
    ];

    // Paper size options
    const paperSizeOptions = [
        { value: 'A4', label: 'A4 (210mm x 297mm)' },
        { value: 'Letter', label: 'Letter (8.5" x 11")' },
        { value: 'Label', label: 'Label Sheet (4" x 6")' }
    ];

    // Labels per page options - Standard label layouts
    const labelsOptions = [
        { value: 12, label: '12 labels per page (3x4)' },
        { value: 16, label: '16 labels per page (4x4)' },
        { value: 21, label: '21 labels per page (3x7)' },
        { value: 24, label: '24 labels per page (4x6) - Standard' },
        { value: 30, label: '30 labels per page (5x6)' },
        { value: 40, label: '40 labels per page (5x8)' },
        { value: 48, label: '48 labels per page (6x8)' }
    ];

    // Load products
    const loadProducts = async (page = pagination.page) => {
        setLoading(true);
        try {
            const params = {
                page,
                limit: pagination.limit,
                search: searchTerm
            };
            const result = await barcodeActions.getBarcodes(params);
            
            if (result.success) {
                setProducts(result.data || []);
                setPagination({
                    page: result.page || page,
                    limit: pagination.limit,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1
                });
            } else {
                toast.error(result.message || 'Failed to load products');
            }
        } catch (error) {
            console.error('Error loading products:', error);
            toast.error('Failed to load products');
        } finally {
            setLoading(false);
        }
    };

    // Load print jobs
    const loadPrintJobs = async (page = 1) => {
        setLoading(true);
        try {
            const result = await barcodeActions.getPrintJobs({ page, limit: 10 });
            
            if (result.success) {
                setPrintJobs(result.data || []);
            } else {
                toast.error(result.message || 'Failed to load print jobs');
            }
        } catch (error) {
            console.error('Error loading print jobs:', error);
            toast.error('Failed to load print jobs');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === 'generate') {
            loadProducts();
        } else if (activeTab === 'history') {
            loadPrintJobs();
        }
    }, [activeTab]);

    // Handle search
    const handleSearch = () => {
        setPagination(prev => ({ ...prev, page: 1 }));
        loadProducts(1);
    };

    // Handle product selection
    const toggleSelectAll = () => {
        if (selectedProducts.length === products.length) {
            setSelectedProducts([]);
        } else {
            setSelectedProducts(products.map(p => p.id));
        }
    };

    const toggleSelectProduct = (productId) => {
        if (selectedProducts.includes(productId)) {
            setSelectedProducts(selectedProducts.filter(id => id !== productId));
        } else {
            setSelectedProducts([...selectedProducts, productId]);
        }
    };

    // Generate barcode for a product
    const generateBarcode = async (productId) => {
        try {
            const result = await barcodeActions.generateBarcode(productId);
            
            if (result.success) {
                toast.success('Barcode generated successfully');
                setProducts(prev => prev.map(p => 
                    p.id === productId 
                        ? { 
                            ...p, 
                            barcode_number: result.data.barcode_number, 
                            barcode_image: result.data.barcode_image 
                          }
                        : p
                ));
            } else {
                toast.error(result.message || 'Failed to generate barcode');
            }
        } catch (error) {
            console.error('Error generating barcode:', error);
            toast.error('Failed to generate barcode');
        }
    };

    // Generate QR code for a product
    const generateQRCode = (product) => {
        setPreviewProduct(product);
        setQrData({
            id: product.id,
            name: product.product_name,
            sku: product.sku || product.barcode_number,
            price: product.selling_price,
            mrp: product.mrp,
            url: `${window.location.origin}/product/${product.id}`
        });
        setShowQRModal(true);
    };

    // Create print job
    const handlePrint = async () => {
        if (selectedProducts.length === 0) {
            toast.warning('Please select at least one product');
            return;
        }

        const confirm = await MySwal.fire({
            title: 'Create Print Job?',
            html: `<p>You are about to create a print job for <strong>${selectedProducts.length}</strong> products with <strong>${copies}</strong> copy/copies each.</p>
                   <p>Total labels: <strong>${selectedProducts.length * copies}</strong></p>
                   <p>Barcode Format: <strong>${barcodeFormat}</strong></p>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, proceed'
        });

        if (confirm.isConfirmed) {
            setLoading(true);
            try {
                const result = await barcodeActions.createPrintJob({
                    product_ids: selectedProducts,
                    copies: copies,
                    paper_size: paperSize,
                    labels_per_page: labelsPerPage,
                    barcode_format: barcodeFormat
                });

                if (result.success) {
                    MySwal.fire({
                        icon: 'success',
                        title: 'Print Job Created',
                        html: `<p>Job Number: <strong>${result.data.job_number}</strong></p>
                               <p>Total Labels: <strong>${result.data.total_labels}</strong></p>
                               <p>Click OK to view print preview</p>`,
                        showCancelButton: true,
                        confirmButtonText: 'View Preview',
                        cancelButtonText: 'Later'
                    }).then((result) => {
                        if (result.isConfirmed && result.data.print_url) {
                            window.open(result.data.print_url, '_blank');
                        }
                    });
                    
                    setSelectedProducts([]);
                } else {
                    toast.error(result.message || 'Failed to create print job');
                }
            } catch (error) {
                console.error('Error creating print job:', error);
                toast.error('Failed to create print job');
            } finally {
                setLoading(false);
            }
        }
    };

    // =====================================================
    // PROFESSIONAL PDF BARCODE GENERATION
    // =====================================================

    /**
     * Load JsBarcode script dynamically
     */
    const loadJsBarcode = () => {
        return new Promise((resolve, reject) => {
            if (window.JsBarcode) {
                resolve();
                return;
            }
            
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js';
            script.onload = () => resolve();
            script.onerror = () => reject(new Error('Failed to load JsBarcode'));
            document.head.appendChild(script);
        });
    };

    /**
     * Generate barcode on canvas
     */
    const generateBarcodeOnCanvas = async (barcodeNumber, format = 'CODE128') => {
        await loadJsBarcode();
        
        const canvas = document.createElement('canvas');
        canvas.width = 300;
        canvas.height = 80;
        
        window.JsBarcode(canvas, barcodeNumber, {
            format: format,
            width: 2,
            height: 50,
            displayValue: true,
            fontSize: 14,
            margin: 10,
            background: '#ffffff',
            lineColor: '#000000'
        });
        
        return canvas.toDataURL('image/png');
    };

    /**
     * Download single barcode as printable PDF
     */
    const downloadSingleBarcodePDF = async (product) => {
        if (!product.barcode_number) {
            toast.warning('Please generate barcode first');
            return;
        }

        try {
            toast.info('Generating barcode PDF...');
            
            // Generate barcode image
            const barcodeImage = await generateBarcodeOnCanvas(product.barcode_number, barcodeFormat);
            
            // Create PDF document
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });
            
            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;
            
            // =====================================================
            // CREATE FULL PAGE OF BARCODE LABELS (24 per page)
            // =====================================================
            
            // Standard label layout: 4 columns x 6 rows = 24 labels
            const cols = 4;
            const rows = 6;
            const margin = 10;
            
            const labelWidth = (pageWidth - (margin * 2)) / cols;
            const labelHeight = (pageHeight - (margin * 2)) / rows;
            
            // Add title
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100, 100, 100);
            doc.text(`Product Barcodes - ${product.product_name}`, margin, 5);
            
            // Generate 24 labels (one full sheet)
            for (let row = 0; row < rows; row++) {
                for (let col = 0; col < cols; col++) {
                    const x = margin + (col * labelWidth);
                    const y = margin + (row * labelHeight);
                    
                    // Draw label border (light gray)
                    doc.setDrawColor(220, 220, 220);
                    doc.setLineWidth(0.1);
                    doc.rect(x, y, labelWidth - 1, labelHeight - 1, 'S');
                    
                    // Product name
                    doc.setFontSize(8);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(40, 40, 40);
                    
                    let productName = product.product_name;
                    if (productName.length > 25) {
                        productName = productName.substring(0, 22) + '...';
                    }
                    doc.text(productName, x + 3, y + 5);
                    
                    // Barcode image
                    try {
                        doc.addImage(
                            barcodeImage,
                            'PNG',
                            x + 5,
                            y + 8,
                            labelWidth - 15,
                            15
                        );
                    } catch (imgError) {
                        // Fallback to text
                        doc.setFontSize(6);
                        doc.text(product.barcode_number, x + 3, y + 15);
                    }
                    
                    // Price
                    doc.setFontSize(10);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(0, 0, 0);
                    doc.text(`₹${formatPrice(product.selling_price)}`, x + 3, y + 30);
                    
                    // MRP
                    doc.setFontSize(6);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(100, 100, 100);
                    doc.text(`MRP: ₹${formatPrice(product.mrp)}`, x + 3, y + 35);
                    
                    // SKU
                    if (product.sku) {
                        doc.setFontSize(5);
                        doc.setTextColor(150, 150, 150);
                        doc.text(`SKU: ${product.sku}`, x + 3, y + 40);
                    }
                }
            }
            
            // Add page numbers
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(6);
                doc.setTextColor(150, 150, 150);
                doc.text(
                    `Page ${i} of ${pageCount} | Generated on ${new Date().toLocaleDateString()}`,
                    pageWidth / 2,
                    pageHeight - 5,
                    { align: 'center' }
                );
            }
            
            // Save PDF
            doc.save(`Barcode_${product.sku || product.id}.pdf`);
            
            toast.success('Barcode PDF downloaded successfully');
        } catch (error) {
            console.error('Error generating barcode PDF:', error);
            toast.error('Failed to generate PDF. Please try again.');
        }
    };

    /**
     * Download multiple barcodes as a single PDF
     */
    const downloadMultipleBarcodesPDF = async () => {
        if (selectedProducts.length === 0) {
            toast.warning('Please select at least one product');
            return;
        }

        const confirm = await MySwal.fire({
            title: 'Generate PDF?',
            html: `<p>You are about to generate a PDF with <strong>${selectedProducts.length}</strong> products.</p>
                   <p>Each product will have <strong>${copies}</strong> copy/copies.</p>
                   <p>Total labels: <strong>${selectedProducts.length * copies}</strong></p>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'Yes, generate PDF'
        });

        if (!confirm.isConfirmed) return;

        setLoading(true);
        toast.info('Generating multi-product PDF...');

        try {
            await loadJsBarcode();
            
            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });
            
            const pageWidth = doc.internal.pageSize.width;
            const pageHeight = doc.internal.pageSize.height;
            
            // Standard label layout
            const cols = 4;
            const rows = 6;
            const margin = 10;
            const labelWidth = (pageWidth - (margin * 2)) / cols;
            const labelHeight = (pageHeight - (margin * 2)) / rows;
            
            let currentCol = 0;
            let currentRow = 0;
            let currentPage = 1;
            
            // Add header
            doc.setFontSize(8);
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(100, 100, 100);
            doc.text(`Multi-Product Barcode Sheet - ${new Date().toLocaleDateString()}`, margin, 5);
            
            // Process each selected product
            for (const productId of selectedProducts) {
                const product = products.find(p => p.id === productId);
                if (!product || !product.barcode_number) continue;
                
                // Generate barcode image for this product
                const barcodeImage = await generateBarcodeOnCanvas(product.barcode_number, barcodeFormat);
                
                // Create specified number of copies
                for (let copy = 0; copy < copies; copy++) {
                    // Check if we need a new page
                    if (currentRow >= rows) {
                        doc.addPage();
                        currentPage++;
                        currentRow = 0;
                        currentCol = 0;
                        
                        // Add header to new page
                        doc.setFontSize(8);
                        doc.setTextColor(100, 100, 100);
                        doc.text(`Multi-Product Barcode Sheet - Page ${currentPage}`, margin, 5);
                    }
                    
                    const x = margin + (currentCol * labelWidth);
                    const y = margin + (currentRow * labelHeight);
                    
                    // Draw label border
                    doc.setDrawColor(220, 220, 220);
                    doc.setLineWidth(0.1);
                    doc.rect(x, y, labelWidth - 1, labelHeight - 1, 'S');
                    
                    // Product name
                    doc.setFontSize(7);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(40, 40, 40);
                    
                    let productName = product.product_name;
                    if (productName.length > 22) {
                        productName = productName.substring(0, 19) + '...';
                    }
                    doc.text(productName, x + 2, y + 4);
                    
                    // Barcode image
                    try {
                        doc.addImage(
                            barcodeImage,
                            'PNG',
                            x + 5,
                            y + 6,
                            labelWidth - 15,
                            12
                        );
                    } catch (imgError) {
                        doc.setFontSize(5);
                        doc.text(product.barcode_number, x + 2, y + 12);
                    }
                    
                    // Price
                    doc.setFontSize(9);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(0, 0, 0);
                    doc.text(`₹${formatPrice(product.selling_price)}`, x + 2, y + 24);
                    
                    // MRP
                    doc.setFontSize(5);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(100, 100, 100);
                    doc.text(`MRP: ₹${formatPrice(product.mrp)}`, x + 2, y + 29);
                    
                    // Move to next position
                    currentCol++;
                    if (currentCol >= cols) {
                        currentCol = 0;
                        currentRow++;
                    }
                }
            }
            
            // Add page numbers
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(6);
                doc.setTextColor(150, 150, 150);
                doc.text(
                    `Page ${i} of ${pageCount} | Generated on ${new Date().toLocaleDateString()}`,
                    pageWidth / 2,
                    pageHeight - 5,
                    { align: 'center' }
                );
            }
            
            doc.save(`Barcodes_Multi_${new Date().getTime()}.pdf`);
            toast.success('PDF generated successfully');
            setSelectedProducts([]);
        } catch (error) {
            console.error('Error generating multi PDF:', error);
            toast.error('Failed to generate PDF');
        } finally {
            setLoading(false);
        }
    };

    // Download QR code as image
    const downloadQR = () => {
        const canvas = document.getElementById('qr-canvas');
        if (canvas) {
            const link = document.createElement('a');
            link.download = `qrcode_${previewProduct?.sku || previewProduct?.id}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        }
    };

    // Print single barcode using browser print
    const printBarcode = (product) => {
        if (!product.barcode_number) {
            toast.warning('Please generate barcode first');
            return;
        }

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>Print Barcode - ${product.product_name}</title>
                    <style>
                        body { 
                            display: flex; 
                            justify-content: center; 
                            align-items: center; 
                            height: 100vh; 
                            margin: 0;
                            font-family: Arial, sans-serif;
                            background-color: #f5f5f5;
                        }
                        .barcode-container {
                            text-align: center;
                            padding: 30px;
                            background: white;
                            border-radius: 10px;
                            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
                        }
                        .product-name {
                            font-size: 16px;
                            font-weight: bold;
                            margin: 15px 0 5px;
                        }
                        .price {
                            font-size: 20px;
                            font-weight: bold;
                            color: #2c3e50;
                        }
                        .mrp {
                            font-size: 12px;
                            color: #7f8c8d;
                            margin-top: 5px;
                        }
                        .barcode-svg {
                            max-width: 300px;
                        }
                        @media print {
                            body { background: white; }
                            .barcode-container { box-shadow: none; }
                        }
                    </style>
                </head>
                <body>
                    <div class="barcode-container">
                        <svg class="barcode-svg" id="barcode"></svg>
                        <div class="product-name">${product.product_name}</div>
                        <div class="price">₹${formatPrice(product.selling_price)}</div>
                        <div class="mrp">MRP: ₹${formatPrice(product.mrp)}</div>
                    </div>
                    <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
                    <script>
                        JsBarcode("#barcode", "${product.barcode_number}", {
                            format: "${barcodeFormat}",
                            width: 2,
                            height: 60,
                            displayValue: true,
                            fontSize: 14,
                            margin: 10
                        });
                        window.onload = function() { window.print(); }
                    </script>
                </body>
            </html>
        `);
        printWindow.document.close();
    };

    // Cancel print job
    const cancelPrintJob = async (jobId) => {
        const confirm = await MySwal.fire({
            title: 'Cancel Print Job?',
            text: 'Are you sure you want to cancel this print job?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, cancel'
        });

        if (confirm.isConfirmed) {
            try {
                const result = await barcodeActions.cancelPrintJob(jobId);
                if (result.success) {
                    toast.success('Print job cancelled');
                    loadPrintJobs();
                } else {
                    toast.error(result.message || 'Failed to cancel job');
                }
            } catch (error) {
                console.error('Error cancelling job:', error);
                toast.error('Failed to cancel job');
            }
        }
    };

    // Tooltip renderer
    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Barcode & QR Code Generator</h4>
                        <h6>Generate and print barcodes for products</h6>
                    </div>

                    <ul className="table-top-head">
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <Link onClick={() => activeTab === 'generate' ? loadProducts() : loadPrintJobs()}>
                                    <RefreshCw />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <Link
                                    className={data ? "active" : ""}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp />
                                </Link>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>

                {/* Tabs */}
                <ul className="nav nav-tabs mb-4">
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'generate' ? 'active' : ''}`}
                            onClick={() => setActiveTab('generate')}
                        >
                            <Printer size={16} className="me-2" />
                            Generate Barcodes
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
                            onClick={() => setActiveTab('history')}
                        >
                            <FileText size={16} className="me-2" />
                            Print History
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'preview' ? 'active' : ''}`}
                            onClick={() => setActiveTab('preview')}
                        >
                            <Eye size={16} className="me-2" />
                            Preview Template
                        </button>
                    </li>
                </ul>

                {/* Generate Tab */}
                {activeTab === 'generate' && (
                    <>
                        {/* Search and Actions */}
                        <div className="card mb-4">
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-3">
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search products..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                            />
                                            <button className="btn btn-primary" onClick={handleSearch}>
                                                <Search size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="col-md-9">
                                        <div className="d-flex justify-content-end gap-2 flex-wrap">
                                            <div className="btn-group me-2">
                                                <button
                                                    className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                    onClick={() => setViewMode('grid')}
                                                >
                                                    <Grid size={16} />
                                                </button>
                                                <button
                                                    className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                    onClick={() => setViewMode('list')}
                                                >
                                                    <List size={16} />
                                                </button>
                                            </div>
                                            
                                            <Select
                                                className="w-auto"
                                                options={formatOptions}
                                                value={formatOptions.find(o => o.value === barcodeFormat)}
                                                onChange={(opt) => setBarcodeFormat(opt?.value || 'CODE128')}
                                                placeholder="Barcode Format"
                                                styles={{
                                                    control: (base) => ({ ...base, minWidth: '150px' })
                                                }}
                                            />
                                            
                                            <select
                                                className="form-select form-select-sm w-auto"
                                                value={copies}
                                                onChange={(e) => setCopies(parseInt(e.target.value))}
                                            >
                                                {[1,2,3,4,5,6,7,8,9,10].map(num => (
                                                    <option key={num} value={num}>{num} copy/copies</option>
                                                ))}
                                            </select>
                                            
                                            <Select
                                                className="w-auto"
                                                options={paperSizeOptions}
                                                value={paperSizeOptions.find(o => o.value === paperSize)}
                                                onChange={(opt) => setPaperSize(opt?.value || 'A4')}
                                                placeholder="Paper Size"
                                                styles={{
                                                    control: (base) => ({ ...base, minWidth: '150px' })
                                                }}
                                            />
                                            
                                            <Select
                                                className="w-auto"
                                                options={labelsOptions}
                                                value={labelsOptions.find(o => o.value === labelsPerPage)}
                                                onChange={(opt) => setLabelsPerPage(opt?.value || 24)}
                                                placeholder="Labels per page"
                                                styles={{
                                                    control: (base) => ({ ...base, minWidth: '150px' })
                                                }}
                                            />
                                            
                                            {selectedProducts.length > 0 && (
                                                <button
                                                    className="btn btn-success"
                                                    onClick={downloadMultipleBarcodesPDF}
                                                    disabled={loading}
                                                >
                                                    <Download size={16} className="me-2" />
                                                    Download PDF ({selectedProducts.length})
                                                </button>
                                            )}
                                            
                                            <button
                                                className="btn btn-primary"
                                                onClick={handlePrint}
                                                disabled={selectedProducts.length === 0 || loading}
                                            >
                                                <Printer size={16} className="me-2" />
                                                Print Job ({selectedProducts.length})
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Products Grid/List */}
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : products.length === 0 ? (
                            <div className="text-center py-5">
                                <FileText size={48} className="text-muted mb-3" />
                                <h5>No products found</h5>
                                <p className="text-muted">Add products to generate barcodes</p>
                            </div>
                        ) : viewMode === 'grid' ? (
                            <div className="row">
                                {products.map(product => (
                                    <div key={product.id} className="col-xl-3 col-lg-4 col-md-6 col-sm-12 mb-4">
                                        <div className="card">
                                            <div className="card-body">
                                                <div className="d-flex justify-content-between align-items-start mb-3">
                                                    <div className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={selectedProducts.includes(product.id)}
                                                            onChange={() => toggleSelectProduct(product.id)}
                                                        />
                                                    </div>
                                                    <div className="dropdown">
                                                        <button className="btn btn-sm btn-outline-secondary" data-bs-toggle="dropdown">
                                                            Actions
                                                        </button>
                                                        <ul className="dropdown-menu">
                                                            <li>
                                                                <button className="dropdown-item" onClick={() => generateBarcode(product.id)}>
                                                                    <Code size={14} className="me-2" />
                                                                    Generate Barcode
                                                                </button>
                                                            </li>
                                                            <li>
                                                                <button className="dropdown-item" onClick={() => generateQRCode(product)}>
                                                                    <Hash size={14} className="me-2" />
                                                                    Generate QR Code
                                                                </button>
                                                            </li>
                                                            {product.barcode_number && (
                                                                <>
                                                                    <li>
                                                                        <button className="dropdown-item" onClick={() => downloadSingleBarcodePDF(product)}>
                                                                            <Download size={14} className="me-2" />
                                                                            Download PDF (Full Sheet)
                                                                        </button>
                                                                    </li>
                                                                    <li>
                                                                        <button className="dropdown-item" onClick={() => printBarcode(product)}>
                                                                            <Printer size={14} className="me-2" />
                                                                            Print Single
                                                                        </button>
                                                                    </li>
                                                                </>
                                                            )}
                                                        </ul>
                                                    </div>
                                                </div>

                                                {/* Barcode Display */}
                                                <div className="text-center mb-3">
                                                    {product.barcode_number ? (
                                                        <div style={{ background: '#fff', padding: '10px' }}>
                                                            <Barcode
                                                                value={product.barcode_number}
                                                                format={barcodeFormat}
                                                                width={2}
                                                                height={50}
                                                                displayValue={true}
                                                                fontSize={12}
                                                                margin={5}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="bg-light p-3 rounded">
                                                            <Camera size={32} className="text-muted mb-2" />
                                                            <p className="text-muted mb-0 small">No barcode generated</p>
                                                            <button 
                                                                className="btn btn-sm btn-outline-primary mt-2"
                                                                onClick={() => generateBarcode(product.id)}
                                                            >
                                                                Generate Now
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>

                                                <h6 className="text-truncate">{product.product_name}</h6>
                                                <p className="text-muted small mb-1">SKU: {product.sku || 'N/A'}</p>
                                                <p className="text-muted small mb-1">
                                                    Barcode: {product.barcode_number || 
                                                        <span className="text-warning">Not generated</span>
                                                    }
                                                </p>
                                                <div className="d-flex justify-content-between">
                                                    <span>Price: ₹{formatPrice(product.selling_price)}</span>
                                                    <span>MRP: ₹{formatPrice(product.mrp)}</span>
                                                </div>
                                                {product.print_count > 0 && (
                                                    <p className="text-muted small mt-2 mb-0">
                                                        Printed: {product.print_count} times
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="card">
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table">
                                            <thead>
                                                <tr>
                                                    <th width="40px">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={selectedProducts.length === products.length}
                                                            onChange={toggleSelectAll}
                                                        />
                                                    </th>
                                                    <th>Product</th>
                                                    <th>SKU</th>
                                                    <th>Barcode</th>
                                                    <th>Barcode Image</th>
                                                    <th>Price</th>
                                                    <th>MRP</th>
                                                    <th>Print Count</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {products.map(product => (
                                                    <tr key={product.id}>
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={selectedProducts.includes(product.id)}
                                                                onChange={() => toggleSelectProduct(product.id)}
                                                            />
                                                        </td>
                                                        <td>{product.product_name}</td>
                                                        <td>{product.sku || 'N/A'}</td>
                                                        <td>
                                                            {product.barcode_number ? (
                                                                <span className="badge bg-light text-dark">
                                                                    {product.barcode_number}
                                                                </span>
                                                            ) : (
                                                                <span className="text-muted">Not generated</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {product.barcode_number ? (
                                                                <div style={{ width: '150px' }}>
                                                                    <Barcode
                                                                        value={product.barcode_number}
                                                                        format={barcodeFormat}
                                                                        width={1}
                                                                        height={30}
                                                                        displayValue={false}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <span className="text-muted">-</span>
                                                            )}
                                                        </td>
                                                        <td>₹{formatPrice(product.selling_price)}</td>
                                                        <td>₹{formatPrice(product.mrp)}</td>
                                                        <td>{product.print_count || 0}</td>
                                                        <td>
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-1"
                                                                onClick={() => generateBarcode(product.id)}
                                                                title="Generate Barcode"
                                                            >
                                                                <Code size={14} />
                                                            </button>
                                                            <button
                                                                className="btn btn-sm btn-outline-info me-1"
                                                                onClick={() => generateQRCode(product)}
                                                                title="Generate QR"
                                                            >
                                                                <Hash size={14} />
                                                            </button>
                                                            {product.barcode_number && (
                                                                <>
                                                                    <button
                                                                        className="btn btn-sm btn-outline-success me-1"
                                                                        onClick={() => printBarcode(product)}
                                                                        title="Print"
                                                                    >
                                                                        <Printer size={14} />
                                                                    </button>
                                                                    <button
                                                                        className="btn btn-sm btn-outline-warning"
                                                                        onClick={() => downloadSingleBarcodePDF(product)}
                                                                        title="Download PDF"
                                                                    >
                                                                        <Download size={14} />
                                                                    </button>
                                                                </>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Pagination */}
                        {pagination.totalPages > 1 && (
                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <div>
                                    Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                                    {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                                </div>
                                <nav>
                                    <ul className="pagination">
                                        <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => loadProducts(pagination.page - 1)}>
                                                Previous
                                            </button>
                                        </li>
                                        {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                            let pageNum = i + 1;
                                            if (pagination.totalPages > 5) {
                                                if (pagination.page <= 3) {
                                                    pageNum = i + 1;
                                                } else if (pagination.page >= pagination.totalPages - 2) {
                                                    pageNum = pagination.totalPages - 4 + i;
                                                } else {
                                                    pageNum = pagination.page - 2 + i;
                                                }
                                            }
                                            return (
                                                <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                                                    <button className="page-link" onClick={() => loadProducts(pageNum)}>
                                                        {pageNum}
                                                    </button>
                                                </li>
                                            );
                                        })}
                                        <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                            <button className="page-link" onClick={() => loadProducts(pagination.page + 1)}>
                                                Next
                                            </button>
                                        </li>
                                    </ul>
                                </nav>
                            </div>
                        )}
                    </>
                )}

                {/* History Tab */}
                {activeTab === 'history' && (
                    <div className="card">
                        <div className="card-body">
                            <div className="table-responsive">
                                <table className="table">
                                    <thead>
                                        <tr>
                                            <th>Job Number</th>
                                            <th>Date</th>
                                            <th>Products</th>
                                            <th>Total Labels</th>
                                            <th>Paper Size</th>
                                            <th>Format</th>
                                            <th>Status</th>
                                            <th>Created By</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {printJobs.map(job => (
                                            <tr key={job.id}>
                                                <td>
                                                    <strong>{job.job_number}</strong>
                                                </td>
                                                <td>{new Date(job.created_at).toLocaleString()}</td>
                                                <td>{job.total_products}</td>
                                                <td>{job.total_products * job.total_copies}</td>
                                                <td>{job.paper_size}</td>
                                                <td>{job.barcode_format || 'CODE128'}</td>
                                                <td>
                                                    <span className={`badge ${
                                                        job.status === 'completed' ? 'bg-success' :
                                                        job.status === 'pending' ? 'bg-warning' :
                                                        job.status === 'cancelled' ? 'bg-danger' : 'bg-secondary'
                                                    }`}>
                                                        {job.status}
                                                    </span>
                                                </td>
                                                <td>{job.created_by_name || 'N/A'}</td>
                                                <td>
                                                    {job.file_path && (
                                                        <button
                                                            className="btn btn-sm btn-outline-primary me-1"
                                                            onClick={() => window.open(job.file_path, '_blank')}
                                                            title="View Print File"
                                                        >
                                                            <Eye size={14} />
                                                        </button>
                                                    )}
                                                    {job.status === 'pending' && (
                                                        <button
                                                            className="btn btn-sm btn-outline-danger"
                                                            onClick={() => cancelPrintJob(job.id)}
                                                            title="Cancel Job"
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* Preview Tab */}
                {activeTab === 'preview' && (
                    <div className="card">
                        <div className="card-body">
                            <h5 className="mb-3">Label Preview - {labelsPerPage} labels per page</h5>
                            <div 
                                className="border p-3"
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: `repeat(${Math.ceil(Math.sqrt(labelsPerPage))}, 1fr)`,
                                    gap: '5px',
                                    minHeight: '500px'
                                }}
                            >
                                {[...Array(Math.min(labelsPerPage, 24))].map((_, i) => (
                                    <div
                                        key={i}
                                        className="border p-2 text-center"
                                        style={{
                                            aspectRatio: '2/1',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <div className="small text-muted">Label {i + 1}</div>
                                        <Barcode
                                            value="SAMPLE123"
                                            format={barcodeFormat}
                                            width={1}
                                            height={20}
                                            displayValue={false}
                                        />
                                        <div className="small">Sample Product</div>
                                        <div className="small fw-bold">₹999.00</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* QR Code Modal */}
            {showQRModal && previewProduct && qrData && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    <Hash size={18} className="me-2" />
                                    QR Code - {previewProduct.product_name}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowQRModal(false)}></button>
                            </div>
                            <div className="modal-body text-center">
                                <div className="mb-4">
                                    <QRCode
                                        id="qr-canvas"
                                        value={JSON.stringify(qrData)}
                                        size={250}
                                        level="H"
                                        includeMargin={true}
                                        bgColor="#ffffff"
                                        fgColor="#000000"
                                    />
                                </div>
                                <div className="text-start bg-light p-3 rounded">
                                    <p className="mb-1"><strong>Product:</strong> {previewProduct.product_name}</p>
                                    <p className="mb-1"><strong>SKU:</strong> {previewProduct.sku || previewProduct.barcode_number || 'N/A'}</p>
                                    <p className="mb-1"><strong>Price:</strong> ₹{formatPrice(previewProduct.selling_price)}</p>
                                    <p className="mb-1"><strong>MRP:</strong> ₹{formatPrice(previewProduct.mrp)}</p>
                                    <p className="mb-0"><strong>QR Data:</strong></p>
                                    <pre className="small bg-white p-2 rounded mt-1" style={{ fontSize: '10px' }}>
                                        {JSON.stringify(qrData, null, 2)}
                                    </pre>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowQRModal(false)}>
                                    Close
                                </button>
                                <button type="button" className="btn btn-primary" onClick={downloadQR}>
                                    <Download size={14} className="me-2" />
                                    Download QR
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BarcodeGenerator;